<template>
  <Error v-if="isErrorLoadingOrders" @retry="emit('getOrders')" />

  <OrdersTable
    v-else-if="expandedAssets"
    :order-items="filteredOrders"
    :filter="filter"
    :offers-isin="offersIsin"
    :offers="initialPublicOffers"
    :loading-delete-order="isLoadingDeleteOrder || isLoadingDeleteOfflineOrder"
    @delete-order="deleteOrder"
    @delete-offline-order="deleteOfflineOrder"
  />
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { useToast } from '@negar/core';

import {
  useDeleteOrderQuery,
  useDeleteOfflineOrderQuery,
  ORDERS_QUERY_KEYS,
  getFilteredOrders,
} from '@core/order';
import { useLiveInitialPublicOffers } from '@core/ipo';
import { OrderItem } from '@libs/api-reference';
import { sleep } from '@libs/utils';
import { useQueryClient } from '@libs/use';

import { ORDER_EXECUTION_TYPE, HELIUM_DEVICE_TYPE } from '@/modules/order';

import Error from '@/components/Error.vue';

import OrdersTable from './OrdersTable.vue';

type OrdersTableTabProps = {
  expandedAssets: boolean;
  filter: string;
  orders: OrderItem[];
  isErrorLoadingOrders: boolean;
};

type OrdersTableTabEmits = {
  (e: 'getOrders'): void;
};

const props = defineProps<OrdersTableTabProps>();
const emit = defineEmits<OrdersTableTabEmits>();

const orders = computed(() => props.orders);

const filteredOrders = computed(() =>
  getFilteredOrders(orders.value, props.filter)
);

const toast = useToast();
const { t } = useI18n();
const queryClient = useQueryClient();

const { offersIsin, initialPublicOffers } = useLiveInitialPublicOffers();

const { deleteOrder, isLoadingDeleteOrder } = useDeleteOrderQuery({
  deviceType: HELIUM_DEVICE_TYPE,
  orderExecutionType: ORDER_EXECUTION_TYPE,
  onSuccess(result, { closeDeleteOrderDialog }) {
    toast.neutral({
      message: t('modules.order.cancelOrderMessage'),
      duration: 3000,
    });

    closeDeleteOrderDialog();
  },
  onReject(error) {
    if (error) {
      for (const { message } of error.response.data) {
        toast.negative({
          message,
          duration: 3000,
        });
      }
    }
  },
});

const { deleteOfflineOrder, isLoadingDeleteOfflineOrder } =
  useDeleteOfflineOrderQuery({
    async onSuccess(result, { closeDeleteOrderDialog }) {
      toast.neutral({
        message: t('modules.order.cancelOrderMessage'),
        duration: 3000,
      });

      await sleep(1000);

      queryClient.invalidateQueries(ORDERS_QUERY_KEYS.orders);

      closeDeleteOrderDialog();
    },
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }
    },
  });
</script>
